/**
 * D3 Tree Type Definitions
 *
 * TypeScript interfaces for D3.js-based interactive family tree visualization
 * Defines D3-specific data structures, layout configurations, and visualization types
 */

import { HierarchyNode, HierarchyPointNode } from 'd3-hierarchy';
import { ZoomTransform } from 'd3-zoom';
import { Person } from './person';
import { TreeNode } from './tree';

/**
 * D3TreeNode - Extended TreeNode with D3 hierarchy data
 * Combines our domain model with D3's hierarchical structure
 * Based on D3 HierarchyPointNode but with custom parent type
 */
export interface D3TreeNode {
  /** Original tree node data */
  data: TreeNode;

  /** X coordinate (horizontal position) */
  x: number;

  /** Y coordinate (vertical position) */
  y: number;

  /** Parent node (null for root) */
  parent: D3TreeNode | null;

  /** Child nodes */
  children?: D3TreeNode[];

  /** Hidden children (collapsed) */
  _children?: D3TreeNode[];

  /** Depth in tree (0 for root) */
  depth: number;

  /** Height (longest path to leaf) */
  height: number;

  /** Is this node currently visible? */
  isVisible?: boolean;

  /** Is this node highlighted? */
  isHighlighted?: boolean;

  /** Is this node part of a highlighted lineage? */
  isInLineage?: boolean;

  /** D3 hierarchy each method */
  each(callback: (node: D3TreeNode) => void): void;
}

/**
 * D3TreeLink - Connection between two nodes
 * Represents parent-child or spouse relationships
 */
export interface D3TreeLink {
  /** Source node */
  source: D3TreeNode;

  /** Target node */
  target: D3TreeNode;

  /** Link type */
  type: 'parent-child' | 'spouse' | 'sibling';

  /** Is this link part of highlighted lineage? */
  isHighlighted?: boolean;

  /** Biological status (for parent-child links) */
  biologicalStatus?: 'biological' | 'adoptive' | 'step' | 'foster' | 'guardian';

  /** Spouse family info (for spouse links) */
  spouseFamilyInfo?: any; // Will be SpouseFamilyInfo from enhanced-tree

  /** Metadata for rendering (e.g., spouse node, siblings for T-shaped connectors) */
  metadata?: {
    spouseNode?: D3TreeNode;
    siblings?: D3TreeNode[];
  };
}

/**
 * TreeLayoutConfig - Configuration for D3 tree layout
 */
export interface TreeLayoutConfig {
  /** Width of the tree layout area */
  width: number;

  /** Height of the tree layout area */
  height: number;

  /** Horizontal spacing between nodes */
  nodeWidth: number;

  /** Vertical spacing between levels */
  nodeHeight: number;

  /** Margins around the tree */
  margin: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };

  /** Layout orientation */
  orientation: 'horizontal' | 'vertical';

  /** Animation duration in milliseconds */
  animationDuration: number;

  /** Enable node collapsing */
  enableCollapse: boolean;

  /** Separation between siblings */
  siblingSeparation: number;

  /** Separation between non-siblings */
  nonSiblingSeparation: number;
}

/**
 * TreeViewConfig - Interactive tree view configuration
 */
export interface TreeViewConfig extends TreeLayoutConfig {
  /** Enable pan functionality */
  enablePan: boolean;

  /** Enable zoom functionality */
  enableZoom: boolean;

  /** Minimum zoom scale */
  minZoom: number;

  /** Maximum zoom scale */
  maxZoom: number;

  /** Initial zoom scale */
  initialZoom: number;

  /** Enable node tooltips */
  enableTooltips: boolean;

  /** Enable minimap overview */
  enableMinimap: boolean;

  /** Enable search highlighting */
  enableSearch: boolean;

  /** Enable lineage highlighting */
  enableLineage: boolean;
}

/**
 * SVGDimensions - SVG viewport dimensions
 */
export interface SVGDimensions {
  /** Total SVG width */
  width: number;

  /** Total SVG height */
  height: number;

  /** Viewbox string for SVG */
  viewBox: string;

  /** Content width (excluding margins) */
  contentWidth: number;

  /** Content height (excluding margins) */
  contentHeight: number;
}

/**
 * TreeLayoutAlgorithm - Available layout algorithms
 */
export type TreeLayoutAlgorithm =
  | 'tree'        // Standard tree layout (D3 tree)
  | 'cluster'     // Cluster/dendrogram layout
  | 'radial'      // Radial/circular layout
  | 'force'       // Force-directed layout
  | 'dagre';      // Directed acyclic graph layout

/**
 * TreeExportFormat - Supported export formats
 */
export type TreeExportFormat = 'svg' | 'png' | 'jpeg' | 'pdf';

/**
 * TreeExportOptions - Configuration for tree export
 */
export interface TreeExportOptions {
  /** Export format */
  format: TreeExportFormat;

  /** File name (without extension) */
  fileName: string;

  /** Image quality (for raster formats, 0-1) */
  quality?: number;

  /** Background color (transparent if not specified) */
  backgroundColor?: string;

  /** Scale factor for high-resolution export */
  scale?: number;

  /** Include margins in export */
  includeMargins?: boolean;

  /** Embed fonts in SVG */
  embedFonts?: boolean;
}

/**
 * ZoomState - Current zoom/pan transformation state
 */
export interface ZoomState {
  /** D3 zoom transform */
  transform: ZoomTransform;

  /** Current zoom scale (1 = 100%) */
  scale: number;

  /** Horizontal translation */
  translateX: number;

  /** Vertical translation */
  translateY: number;
}

/**
 * NodePosition - Calculated position for a tree node
 */
export interface NodePosition {
  /** Person ID */
  personId: string;

  /** X coordinate */
  x: number;

  /** Y coordinate */
  y: number;

  /** Depth level */
  depth: number;

  /** Is expanded */
  isExpanded: boolean;
}

/**
 * LinkPath - Path data for rendering a link
 */
export interface LinkPath {
  /** SVG path data string */
  d: string;

  /** Source node ID */
  sourceId: string;

  /** Target node ID */
  targetId: string;

  /** Link type */
  type: 'parent-child' | 'spouse' | 'sibling';
}

/**
 * TreeRenderData - Complete data package for rendering
 */
export interface TreeRenderData {
  /** All nodes to render */
  nodes: D3TreeNode[];

  /** All links to render */
  links: D3TreeLink[];

  /** SVG dimensions */
  dimensions: SVGDimensions;

  /** Root node */
  root: D3TreeNode;

  /** Total visible nodes */
  visibleNodeCount: number;

  /** Total links */
  linkCount: number;
}

/**
 * TreeSearchResult - Result from tree search
 */
export interface TreeSearchResult {
  /** Found person */
  person: Person;

  /** Tree node */
  node: D3TreeNode;

  /** Path from root to this node */
  pathFromRoot: D3TreeNode[];

  /** Depth in tree */
  depth: number;

  /** Relevance score (0-1) */
  relevance: number;
}

/**
 * LineagePathResult - Calculated path between two persons
 */
export interface LineagePathResult {
  /** Start person ID */
  startPersonId: string;

  /** End person ID */
  endPersonId: string;

  /** Nodes in the path */
  path: D3TreeNode[];

  /** Links in the path */
  links: D3TreeLink[];

  /** Relationship description (e.g., "Great Grandfather") */
  relationshipDescription: string;

  /** Number of generations between */
  generationDistance: number;

  /** Total steps in path */
  pathLength: number;
}

/**
 * TreeStatistics - Extended statistics for D3 tree
 */
export interface D3TreeStatistics {
  /** Total nodes in tree */
  totalNodes: number;

  /** Visible nodes (not collapsed) */
  visibleNodes: number;

  /** Hidden nodes (collapsed) */
  hiddenNodes: number;

  /** Maximum depth */
  maxDepth: number;

  /** Average depth */
  averageDepth: number;

  /** Total links */
  totalLinks: number;

  /** Tree width (horizontal span) */
  treeWidth: number;

  /** Tree height (vertical span) */
  treeHeight: number;

  /** Layout algorithm used */
  algorithm: TreeLayoutAlgorithm;
}

/**
 * NodeInteraction - Node interaction event data
 */
export interface NodeInteraction {
  /** Node that was interacted with */
  node: D3TreeNode;

  /** Person data */
  person: Person;

  /** Interaction type */
  type: 'click' | 'hover' | 'contextmenu' | 'doubleclick';

  /** Mouse event */
  event: MouseEvent | React.MouseEvent;

  /** Node element */
  element: SVGGElement | null;
}

/**
 * TreeAnimation - Animation configuration
 */
export interface TreeAnimation {
  /** Enable animations */
  enabled: boolean;

  /** Animation duration (ms) */
  duration: number;

  /** Easing function */
  easing: 'linear' | 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out';

  /** Animate on initial load */
  animateOnLoad: boolean;

  /** Animate expand/collapse */
  animateCollapse: boolean;

  /** Animate zoom/pan */
  animateZoom: boolean;
}

/**
 * TreePerformanceConfig - Performance optimization settings
 */
export interface TreePerformanceConfig {
  /** Enable virtual rendering for large trees */
  enableVirtualRendering: boolean;

  /** Node count threshold for virtual rendering */
  virtualRenderingThreshold: number;

  /** Debounce zoom events (ms) */
  zoomDebounce: number;

  /** Debounce pan events (ms) */
  panDebounce: number;

  /** Use Canvas rendering instead of SVG for large trees */
  useCanvasForLargeTrees: boolean;

  /** Canvas rendering threshold (node count) */
  canvasThreshold: number;

  /** Lazy load collapsed subtrees */
  lazyLoadCollapsed: boolean;
}

/**
 * FanChartConfig - Configuration for fan/radial chart layout
 */
export interface FanChartConfig {
  /** Inner radius (center circle size) */
  innerRadius: number;

  /** Outer radius (total fan size) */
  outerRadius: number;

  /** Start angle (radians) */
  startAngle: number;

  /** End angle (radians) */
  endAngle: number;

  /** Spacing between generations */
  generationSpacing: number;

  /** Spacing between nodes in same generation */
  nodeSpacing: number;
}

/**
 * Default configuration values
 */
export const DEFAULT_TREE_CONFIG: TreeViewConfig = {
  width: 1200,
  height: 800,
  nodeWidth: 180,  // Original card width
  nodeHeight: 80,  // Original card height
  margin: {
    top: 40,
    right: 40,
    bottom: 40,
    left: 40,
  },
  orientation: 'vertical',
  animationDuration: 500,
  enableCollapse: true,
  siblingSeparation: 1.5,  // Increased spacing between siblings to avoid overlap
  nonSiblingSeparation: 2.5,  // Increased spacing between non-siblings
  enablePan: true,
  enableZoom: true,
  minZoom: 0.1,
  maxZoom: 3,
  initialZoom: 1,
  enableTooltips: true,
  enableMinimap: false,
  enableSearch: true,
  enableLineage: true,
};

export const DEFAULT_EXPORT_OPTIONS: TreeExportOptions = {
  format: 'svg',
  fileName: 'family-tree',
  quality: 1,
  scale: 2,
  includeMargins: true,
  embedFonts: true,
};

export const DEFAULT_PERFORMANCE_CONFIG: TreePerformanceConfig = {
  enableVirtualRendering: true,
  virtualRenderingThreshold: 500,
  zoomDebounce: 50,
  panDebounce: 50,
  useCanvasForLargeTrees: false,
  canvasThreshold: 2000,
  lazyLoadCollapsed: true,
};
